import React, {
  FunctionComponent,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { OrderList } from '../../lib/ssp/types';
import Loader from '../Loader';
import LogoutButton from '../LogoutButton';
import Order from '../Order';
import { PageSection } from '../PageSection';
import ErrorMessage, { ErrorWrapper } from '../self-serve/ErrorMessage';
import WithHelpCenterChrome, {
  WithHelpCenterChromeOwnProps,
} from '../WithHelpCenterChrome';
import WithNavigationTopBar from '../WithNavigationTopBar';

import { INITIAL_PAGE_SIZE, PER_PAGE } from './constants';
import { SeeMoreAction } from './styled';
import { calculateShowMoreCount, paginateOrders } from './utils';

export type OrdersPageProps = WithHelpCenterChromeOwnProps & {
  orders: OrderList;
  isFetchError: boolean;
  isLoading: boolean;
  targetOrderName?: string;
  onLogout: () => void;
};

/**
 * TODO: Enable search after UX decision
 */
const OrdersPage: FunctionComponent<OrdersPageProps> = ({
  orders = {},
  isLoading,
  isFetchError,
  targetOrderName,
  onLogout,
  ...props
}) => {
  const { t } = useTranslation();
  const [numOfOrdersDisplayed, setNumOfOrdersDisplayed] =
    useState(INITIAL_PAGE_SIZE);

  const ordersArr = useMemo(() => Object.values(orders).reverse(), [orders]);

  const { query } = useRouter();
  const justLoggedIn = query?.justLoggedIn === 'true';

  useEffect(() => {
    const targetOrderNameIndex = ordersArr.findIndex((order) => {
      if (targetOrderName != null && justLoggedIn) {
        return order.name.toLowerCase().includes(targetOrderName.toLowerCase());
      }
      return true;
    });

    if (
      targetOrderNameIndex >= 0 &&
      targetOrderNameIndex >= numOfOrdersDisplayed
    ) {
      setNumOfOrdersDisplayed(targetOrderNameIndex + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersArr, targetOrderName]);

  const handleOrdersLoadMore = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();

    setNumOfOrdersDisplayed(numOfOrdersDisplayed + PER_PAGE);
  };

  let content;

  if (isLoading) {
    content = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <Loader size="lg" />
      </div>
    );
  } else if (isFetchError) {
    content = (
      <ErrorWrapper>
        <ErrorMessage hasIcon size="large">
          {t('errorFetchingOrder')}
        </ErrorMessage>
      </ErrorWrapper>
    );
  } else {
    content = (
      <>
        {paginateOrders(ordersArr, numOfOrdersDisplayed).map((order, index) => {
          let isDefaultOpen = index === 0;

          if (targetOrderName != null && (targetOrderName ?? '')) {
            isDefaultOpen =
              targetOrderName.toLowerCase() === order.name.toLowerCase();
          }

          return (
            <Order
              key={order.name}
              order={order}
              defaultOpen={isDefaultOpen && justLoggedIn}
            />
          );
        })}
        {ordersArr.length > numOfOrdersDisplayed && (
          <SeeMoreAction onClick={handleOrdersLoadMore}>
            {t('ordersListShowMore', {
              count: calculateShowMoreCount(
                ordersArr.length,
                numOfOrdersDisplayed,
              ),
            })}
          </SeeMoreAction>
        )}
      </>
    );
  }

  return (
    <WithHelpCenterChrome {...props}>
      <WithNavigationTopBar
        {...props}
        isSearchActivated={false}
        levels={[
          {
            title: t('ordersPageBreadcrumb'),
          },
        ]}
      />
      <PageSection
        title={t('ordersPageMetaTitle')}
        more={<LogoutButton onClick={onLogout} />}
      >
        {content}
      </PageSection>
    </WithHelpCenterChrome>
  );
};

export default OrdersPage;
